import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, FileText, Phone, Info } from 'lucide-react';

const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/experience', label: 'Pricing', icon: FileText },
    { path: '/waiver', label: 'Waiver', icon: FileText },
    { path: '/about', label: 'About', icon: Info },
    { path: '/contact', label: 'Contact', icon: Phone },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed ${isScrolled ? 'top-0' : 'top-10 sm:top-12' } left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-gradient-to-r from-purple-1200 to-purple-500 shadow-sm' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/assets/logo.png" 
              alt="Immersa Entertainment" 
              className="h-14 sm:h-16 w-auto transition-transform group-hover:scale-105"
              onError={(e) => {
                // Fallback if image doesn't exist
                const target = e.currentTarget;
                target.style.display = 'none';
                // Show text logo instead
                const textLogo = target.nextElementSibling as HTMLElement;
                if (textLogo) {
                  textLogo.style.display = 'block';
                }
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-6">
              {navItems.filter(item => item.path !== '/waiver').map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center space-x-2 ${
                    isActive(path)
                      ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </Link>
              ))}
              
              {/* Highlighted Waiver Button */}
              <Link
                to="/waiver"
                className={`px-6 py-3 rounded-full text-sm font-bold transition-all duration-300 flex items-center space-x-2 transform hover:scale-105 ${
                  isActive('/waiver')
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-xl'
                    : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 shadow-lg hover:shadow-purple-500/25'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Start Waiver</span>
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="bg-white/10 backdrop-blur-sm inline-flex items-center justify-center p-3 rounded-lg text-gray-400 hover:text-white hover:bg-white/20 transition-all duration-300 shadow-lg"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-3 pt-3 pb-4 space-y-2 bg-black/30 backdrop-blur-xl rounded-xl mt-3 mb-2 mx-2 shadow-2xl">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 flex items-center space-x-3 ${
                    isActive(path)
                      ? 'bg-white/20 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{label}</span>
                </Link>
              ))}
              
              {/* Mobile Waiver Button */}
              <Link
                to="/waiver"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 rounded-lg text-base font-bold bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-3 mt-4 shadow-xl"
              >
                <FileText className="w-5 h-5" />
                <span>Start Waiver - Your VR Passport</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;